import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";

import Swal from "sweetalert2";
import toast from "react-hot-toast";
import HomeService from "../Services/HomeService";
import HttpClientXml from "../utils/HttpClientXml";

const headLineStyle = {
  textAlign: "center",
  fontSize: "30px",
  color: "#868e96",
  margin: "15px",
  fontWeight: "bold",
};

function ManageHighlightedProject() {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projectID, setProjectID] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState({
    title: "",
    shortDescription: "",
    price: "",
    image: "",
    type: "",
    locationText: "",
    locationUrl: "",
    propertyUrl: "",
  });

  const columns = [
    { name: "S.No.", selector: (row) => row.sln, sortable: true },
    {
      name: "Image",
      cell: (row) => (
        <img
          src={row.image || "placeholder-image-url"}
          alt={row.title}
          style={{ maxHeight: "100px", padding: "5px 0" }}
        />
      ),
    },
    { name: "Title", selector: (row) => row.title, wrap: true },
    {
      name: "Short Description",
      selector: (row) => row.shortDescription,
      wrap: true,
    },
    { name: "Price", selector: (row) => row.price, wrap: true },
    { name: "Type", selector: (row) => row.type, wrap: true },
    { name: "Location", selector: (row) => row.locationText, wrap: true },

    {
      name: "Action",
      cell: (row) => (
        <>
          <span title="Edit" onClick={() => handleEdit(row)}>
            <i
              className="fa-solid fa-pen-to-square"
              style={{
                cursor: "pointer",
                padding: "5px",
                marginRight: "5px",
                fontSize: "1.5em",
                color: "rgb(133, 218, 79)",
                backgroundColor: "#E9F3F7",
              }}
            ></i>
          </span>
          <span title="Delete" onClick={() => handleDelete(row.id)}>
            <i
              className="fa-solid fa-trash-can text-danger"
              style={{
                cursor: "pointer",
                padding: "5px",
                marginRight: "5px",
                fontSize: "1.5em",
                color: "#477DA5",
                backgroundColor: "#E9F3F7",
              }}
            ></i>
          </span>
        </>
      ),
    },
  ];

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const res = await HomeService.getHighlightedProject();
      console.log("Highlighted Projects:", res.data);
      const arr = res?.data.map((ele, ind) => ({
        sln: ind + 1,
        title: ele.title,
        shortDescription: ele.shortDescription,
        price: ele.price,
        image: ele.image,
        type: ele.type || "",
        locationText: ele.locationText || "",
        locationUrl: ele.locationUrl || "",
        propertyUrl: ele.propertyUrl || "",
        id: ele._id,
      }));
      setAllData(arr);
    } catch (error) {
      console.error("Error fetching highlighted projects:", error);
      toast.error("Failed to load highlighted projects!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const handleEdit = (row) => {
    setIsEdit(true);
    setEditData({
      title: row.title,
      shortDescription: row.shortDescription,
      price: row.price,
      image: row.image,
      type: row.type || "",
      locationText: row.locationText || "",
      locationUrl: row.locationUrl || "",
      propertyUrl: row.propertyUrl || "",
    });
    setProjectID(row.id);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete Highlighted Project?",
      text: "Are you sure you want to delete this highlighted project?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });
    if (result.isConfirmed) {
      try {
        await HomeService.deleteHighlightedProject(id);
        Swal.fire(
          "Deleted!",
          "Highlighted project has been deleted.",
          "success",
        );
        fetchAllData();
      } catch (error) {
        Swal.fire("Error!", "Failed to delete highlighted project.", "error");
      }
    }
  };

  const handleImageUpload = async (e) => {
    const data = new FormData();
    data.append("image", e.target.files[0]);
    try {
      const res = await HttpClientXml.fileUplode("image-upload", "POST", data);
      setEditData({ ...editData, image: res.image });
      toast.success("Image uploaded successfully!");
    } catch (error) {
      toast.error("Failed to upload image.");
      console.error("Error uploading image:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!editData.title.trim()) {
      toast.error("Project title is required!");
      return;
    }
    if (!editData.shortDescription.trim()) {
      toast.error("Short description is required!");
      return;
    }
    if (!editData.price.trim()) {
      toast.error("Price is required!");
      return;
    }
    if (!editData.image) {
      toast.error("Project image is required!");
      return;
    }

    try {
      const data = {
        title: editData.title,
        shortDescription: editData.shortDescription,
        price: editData.price,
        image: editData.image,
        type: editData.type,
        locationText: editData.locationText,
        locationUrl: editData.locationUrl,
        propertyUrl: editData.propertyUrl,
      };

      if (isEdit) {
        await HomeService.updateHighlightedProject(projectID, data);
        toast.success("Highlighted project updated successfully!");
      }

      setIsEdit(false);
      setEditData({
        title: "",
        shortDescription: "",
        price: "",
        image: "",
        type: "",
        locationText: "",
        locationUrl: "",
        propertyUrl: "",
      });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to save highlighted project.");
      console.error("Error saving highlighted project:", error);
    }
  };

  const cancelEdit = () => {
    setIsEdit(false);
    setEditData({
      title: "",
      shortDescription: "",
      price: "",
      image: "",
      type: "",
      locationText: "",
      locationUrl: "",
      propertyUrl: "",
    });
    setProjectID("");
  };

  return (
    <>
      <div className="main_wrap" style={{ marginTop: "6rem" }}>
        <div className="container-fluid">
          {isEdit && (
            <section className="chartBox_area">
              <h2>Edit Highlighted Project</h2>
              <hr />
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-12 mt-3">
                    <label htmlFor="projectTitle">
                      Project Title<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="projectTitle"
                      className="form-control"
                      value={editData.title}
                      onChange={(e) =>
                        setEditData({ ...editData, title: e.target.value })
                      }
                      placeholder="Enter project title"
                      required
                    />
                  </div>

                  <div className="col-md-12 mt-3">
                    <label htmlFor="shortDescription">
                      Short Description<span className="text-danger">*</span>
                    </label>
                    <textarea
                      id="shortDescription"
                      className="form-control"
                      value={editData.shortDescription}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          shortDescription: e.target.value,
                        })
                      }
                      placeholder="Enter short description"
                      rows="3"
                      required
                    />
                  </div>

                  <div className="col-md-12 mt-3">
                    <label htmlFor="price">
                      Price<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="price"
                      className="form-control"
                      value={editData.price}
                      onChange={(e) =>
                        setEditData({ ...editData, price: e.target.value })
                      }
                      placeholder="Enter price (e.g., 50 LKH)"
                      required
                    />
                  </div>

                  <div className="col-md-12 mt-3">
                    <label htmlFor="projectImage">
                      Project Image<span className="text-danger">*</span>
                    </label>
                    {editData.image && (
                      <div style={{ marginBottom: "10px" }}>
                        <img src={editData.image} alt="Project" width="100" />
                      </div>
                    )}
                    <input
                      type="file"
                      id="projectImage"
                      className="form-control"
                      onChange={handleImageUpload}
                      accept="image/*"
                    />
                  </div>

                  <div className="col-md-12 mt-3">
                    <label htmlFor="type">
                      Type
                    </label>
                    <input
                      type="text"
                      id="type"
                      className="form-control"
                      value={editData.type}
                      onChange={(e) =>
                        setEditData({ ...editData, type: e.target.value })
                      }
                      placeholder="Enter project type"
                    />
                  </div>

                  <div className="col-md-12 mt-3">
                    <label htmlFor="locationText">
                      Location Text
                    </label>
                    <input
                      type="text"
                      id="locationText"
                      className="form-control"
                      value={editData.locationText}
                      onChange={(e) =>
                        setEditData({ ...editData, locationText: e.target.value })
                      }
                      placeholder="Enter location text"
                    />
                  </div>

                  <div className="col-md-12 mt-3">
                    <label htmlFor="locationUrl">
                      Location URL
                    </label>
                    <input
                      type="text"
                      id="locationUrl"
                      className="form-control"
                      value={editData.locationUrl}
                      onChange={(e) =>
                        setEditData({ ...editData, locationUrl: e.target.value })
                      }
                      placeholder="Enter location URL"
                    />
                  </div>

                  <div className="col-md-12 mt-3">
                    <label htmlFor="propertyUrl">
                      Property URL
                    </label>
                    <input
                      type="text"
                      id="propertyUrl"
                      className="form-control"
                      value={editData.propertyUrl}
                      onChange={(e) =>
                        setEditData({ ...editData, propertyUrl: e.target.value })
                      }
                      placeholder="Enter property URL"
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <button
                    type="submit"
                    className="btn btn-primary mt-3"
                    style={{ marginRight: "10px" }}
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary mt-3"
                    onClick={cancelEdit}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </section>
          )}

          <div className="mt-5">
            <div style={headLineStyle} className="page-headline">
              Manage Highlighted Projects
            </div>
            {loading ? (
              <p>Loading...</p>
            ) : (
              <DataTable
                columns={columns}
                data={allData}
                pagination
                responsive
                highlightOnHover
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default ManageHighlightedProject;
